/**
 * Minimal focus trap that cycles Tab/Shift+Tab among interactive elements
 * within a given container. No external dependencies required.
 */
export class FocusTrap {
  private readonly container: HTMLElement;
  private readonly _previouslyFocused: HTMLElement | null;

  constructor(container: HTMLElement) {
    this.container = container;
    this._previouslyFocused = typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
  }

  install(): void {
    this.container.addEventListener('keydown', this._onKeydown);
  }

  uninstall(): void {
    this.container.removeEventListener('keydown', this._onKeydown);
    this._previouslyFocused?.focus();
  }

  private readonly _onKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Tab') return;

    const selectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusable = Array.from(this.container.querySelectorAll<HTMLElement>(selectors));
    if (focusable.length === 0) return;

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;

    if (typeof document === 'undefined') return;
    const active = document.activeElement;

    if (e.shiftKey) {
      if (active === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
}